import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import Button from 'components/common/buttons/Button';
import { getFontFaceFromStyle } from 'support/textUtils';
import ActionsList from 'components/common/ActionsList';
import { constants } from 'aliases';
import TextLabel from 'components/common/TextLabel';

const TITLE_FONT_SIZE = 63;
const DEFAULT_ACTIONS_TOP_MARGIN = 90;
const MIN_BUTTON_WIDTH = 351;
const DEFAULT_BUTTON_PADDING = 18;
const DEFAULT_BUTTON_PADDINGH = 84;
const DEFAULT_OPACITY = 1;
const TITLE_WRAP_WIDTH = 1840;

const CONFIRM = '_confirm';
const CANCEL = '_cancel';

export type ModalProps = Partial<
  Pick<
    AbstractModalTemplateSpec,
    | 'title'
    | 'confirmLabel'
    | 'cancelLabel'
    | 'onConfirm'
    | 'onCancel'
    | 'onBack'
    | 'bgAlpha'
    | 'bgColor'
  >
>;

export interface AbstractModalTemplateSpec
  extends Lightning.Component.TemplateSpec {
  title: string;
  confirmLabel: string;
  cancelLabel: string;
  bgAlpha: number;
  bgColor: 'modalBackground' | 'black';
  onConfirm: (() => void) | null;
  onCancel: (() => void) | null;
  onBack: (() => void) | null;
  Title: typeof TextLabel;
  Actions: typeof ActionsList;
}

export default abstract class AbstractModal
  extends Lightning.Component<AbstractModalTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractModalTemplateSpec>
{
  protected _confirmLabel = '';
  protected _cancelLabel = '';
  protected _onConfirm: AbstractModalTemplateSpec['onConfirm'] = null;
  protected _onCancel: AbstractModalTemplateSpec['onCancel'] = null;
  protected _onBack: AbstractModalTemplateSpec['onBack'] = null;
  protected _bgAlpha = 1;
  protected _bgColor = 'modalBackground';
  protected _close = false;

  protected _Title = this.getByRef('Title')!;
  protected _Actions = this.getByRef('Actions')!;

  override _setup() {
    this.bgAlpha = DEFAULT_OPACITY;
    this.bgColor = 'modalBackground';
  }

  // Update properties from props if provided; otherwise, retain values set in _setup
  public update(props: ModalProps) {
    const {
      title = this.title,
      cancelLabel = this.cancelLabel,
      confirmLabel = this.confirmLabel,
      bgAlpha = this._bgAlpha,
      bgColor = this._bgColor,
      onConfirm = this.onConfirm,
      onCancel = this.onCancel,
      onBack = this.onBack,
    } = props;

    this.title = title;
    this.bgAlpha = bgAlpha;
    this.bgColor = bgColor as AbstractModalTemplateSpec['bgColor'];
    this.confirmLabel = confirmLabel;
    this.cancelLabel = cancelLabel;
    this.onConfirm = onConfirm;
    this.onCancel = onCancel;
    this.onBack = onBack;
  }

  get title() {
    return this._Title.text?.text ?? '';
  }

  set title(title: AbstractModalTemplateSpec['title']) {
    this._Title.patch({ text: { text: title } });
  }

  get confirmLabel() {
    return this._confirmLabel;
  }

  set confirmLabel(confirmLabel: AbstractModalTemplateSpec['confirmLabel']) {
    this._confirmLabel = confirmLabel;
    this.updateActions();
  }

  get cancelLabel() {
    return this._cancelLabel;
  }

  set cancelLabel(cancelLabel: AbstractModalTemplateSpec['cancelLabel']) {
    this._cancelLabel = cancelLabel;
    this.updateActions();
  }

  get onConfirm() {
    return this._onConfirm;
  }

  set onConfirm(onConfirm: AbstractModalTemplateSpec['onConfirm']) {
    this._onConfirm = onConfirm;
  }

  get onCancel() {
    return this._onCancel;
  }

  set onCancel(onCancel: AbstractModalTemplateSpec['onCancel']) {
    this._onCancel = onCancel;
  }

  get onBack() {
    return this._onBack;
  }

  set onBack(onBack: AbstractModalTemplateSpec['onBack']) {
    this._onBack = onBack;
  }

  set bgAlpha(value: AbstractModalTemplateSpec['bgAlpha']) {
    this._bgAlpha = value;
    this.setBgColor();
  }

  set bgColor(value: AbstractModalTemplateSpec['bgColor']) {
    this._bgColor = value;
    this.setBgColor();
  }

  static override _template(): Lightning.Component.Template<AbstractModalTemplateSpec> {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: Colors('modalBackground').get(),
      flex: {
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      Title: {
        type: TextLabel,
        text: {
          fontSize: TITLE_FONT_SIZE,
          fontFace: getFontFaceFromStyle('bold'),
          textAlign: 'center',
          wordWrapWidth: TITLE_WRAP_WIDTH,
        },
      },
      Actions: {
        type: ActionsList,
        flexItem: { marginTop: DEFAULT_ACTIONS_TOP_MARGIN },
        signals: { [CONFIRM]: true, [CANCEL]: true },
      },
      alpha: 0,
      zIndex: constants.ui.zIndex.modalLayer,
    };
  }

  hide() {
    this.patch({ alpha: 0 });
  }

  show() {
    this.patch({ alpha: 1 });
  }

  override _getFocused() {
    return this._Actions;
  }

  override _focus() {
    this._close = false;
  }

  override _handleKey() {
    // Do not allow the user to bypass the modal
    return true;
  }

  override _handleBack() {
    this._onBack?.();
    this.close();
  }

  private updateActions() {
    this._Actions.clearActions();

    if (this.confirmLabel) {
      this._Actions.addAction(this.buildButton(this.confirmLabel, CONFIRM));
    }

    if (this.cancelLabel) {
      this._Actions.addAction(this.buildButton(this.cancelLabel, CANCEL));
    }

    if (this.active) {
      this._Actions._refocus();
    }
  }

  protected close() {
    this._close = true;
    AppData?.modalService.close();
  }

  private _confirm() {
    this.onConfirm?.();
    this.close();
  }

  private _cancel() {
    this.onCancel?.();
    this.close();
  }

  private buildButton(label: string, action: string) {
    return {
      type: Button,
      label,
      action,
      minWidth: MIN_BUTTON_WIDTH,
      padding: DEFAULT_BUTTON_PADDING,
      paddingH: DEFAULT_BUTTON_PADDINGH,
      passSignals: { [action]: true },
      signals: {
        $onHover: '$onHover',
      },
      zIndex: constants.ui.zIndex.modalLayer + 1,
    };
  }

  private setBgColor() {
    this.color = Colors(this._bgColor).alpha(this._bgAlpha).get();
  }
}
